import React, { useState, useEffect, useCallback } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Typography,
    Alert,
    CircularProgress,
    Box,
    Grid,
    Chip,
    Divider,
    FormControlLabel,
    Checkbox,
    InputAdornment
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { investmentService } from "../investments/investmentService";
import { usersService } from "../users/usersService";
import { emailService } from "../services/emailService";
import { LoggerService } from "../services/LoggerService";
import { formatCurrency } from "../capital/capitalUtils";
import {
    AccountBalance,
    CheckCircle
} from "@mui/icons-material";

const InvestmentForm = ({ opportunity, open, onClose, onInvestmentComplete }) => {
    const { currentUser } = useAuth();
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [amount, setAmount] = useState("");
    const [userBalance, setUserBalance] = useState(0);
    const [kycStatus, setKycStatus] = useState("pending");
    const [confirmedInvestment, setConfirmedInvestment] = useState(null);
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [eligibilityChecked, setEligibilityChecked] = useState(false);

    // Verificar elegibilidad del usuario
    const checkUserEligibility = useCallback(async () => {
        try {
            if (!currentUser || !opportunity) return false;

            // 1. Verificar KYC
            const userProfileData = await usersService.getUserProfile(currentUser.uid);
            if (!userProfileData) {
                setError("Perfil de usuario no encontrado");
                return false;
            }

            setKycStatus(userProfileData.kycStatus || "pending");
            setUserBalance(userProfileData.balance || 0);

            // 2. Verificar si KYC está aprobado
            if (userProfileData.kycStatus !== "approved") {
                setError("Debes completar la verificación KYC antes de invertir");
                return false;
            }

            // 3. Verificar si la oportunidad está disponible
            if (opportunity.status !== "active" && opportunity.status !== "published") {
                setError("Esta oportunidad no está disponible para inversión");
                return false;
            }

            // 4. Verificar si el usuario ya invirtió en esta oportunidad
            const existingInvestments = await investmentService.getUserInvestments(currentUser.uid);
            const hasInvested = existingInvestments.some(inv => 
                inv.opportunityId === opportunity.id && 
                ["active", "pending", "completed"].includes(inv.status)
            );

            if (hasInvested) {
                setError("Ya has invertido en esta oportunidad");
                return false;
            }

            setEligibilityChecked(true);
            return true;

        } catch (error) {
            setError("Error al verificar elegibilidad");
            LoggerService.log("system", "error", {
                error: error.message,
                stack: error.stack,
                service: "InvestmentForm",
                context: "checkUserEligibility",
                userId: currentUser?.uid
            });
            return false;
        }
    }, [currentUser, opportunity]);

    useEffect(() => {
        if (open && currentUser && opportunity) {
            checkUserEligibility();
        }
    }, [open, currentUser, opportunity, checkUserEligibility]);

    // Validar monto de inversión con validación mejorada de balance
    const validateInvestmentAmount = (amount) => {
        const numAmount = parseFloat(amount);
        
        if (!amount || numAmount <= 0) {
            return t("pleaseEnterValidAmount") || "Por favor ingresa un monto válido";
        }

        if (numAmount < (opportunity?.minimumInvestment || 1000)) {
            return t("minimumInvestmentAmount", { amount: formatCurrency(opportunity?.minimumInvestment || 1000) }) || 
                   `El monto mínimo de inversión es ${formatCurrency(opportunity?.minimumInvestment || 1000)}`;
        }

        if (numAmount > (opportunity?.maximumInvestment || 1000000)) {
            return t("maximumInvestmentAmount", { amount: formatCurrency(opportunity?.maximumInvestment || 1000000) }) || 
                   `El monto máximo de inversión es ${formatCurrency(opportunity?.maximumInvestment || 1000000)}`;
        }

        // Validación crítica de balance
        if (numAmount > userBalance) {
            LoggerService.log("business", "insufficient_balance", {
                userId: currentUser?.uid,
                requestedAmount: numAmount,
                availableBalance: userBalance,
                opportunityId: opportunity?.id
            });
            
            return t("insufficientBalance", { balance: formatCurrency(userBalance) }) || 
                   `Saldo insuficiente. Tu saldo disponible es ${formatCurrency(userBalance)}`;
        }

        // Validación de balance mínimo después de inversión
        const remainingBalance = userBalance - numAmount;
        const minimumBalance = 100; // Balance mínimo recomendado
        
        if (remainingBalance < minimumBalance) {
            return t("lowBalanceWarning", { remaining: formatCurrency(remainingBalance) }) || 
                   `Advertencia: Tu saldo restante será ${formatCurrency(remainingBalance)}. Se recomienda mantener un saldo mínimo.`;
        }

        return null;
    };

    // Confirmar inversión con validación mejorada y logging detallado
    const handleConfirmInvestment = async () => {
        // Validación previa
        const validationError = validateInvestmentAmount(amount);
        if (validationError) {
            setError(validationError);
            return;
        }

        if (!termsAccepted) {
            setError(t("termsRequired") || "Debes aceptar los términos y condiciones");
            return;
        }

        // Verificación final de balance antes de procesar
        const finalBalanceCheck = await usersService.getUserProfile(currentUser.uid);
        if (finalBalanceCheck.balance < parseFloat(amount)) {
            setError(t("balanceChanged") || "Tu saldo ha cambiado. Por favor, verifica el monto disponible.");
            setUserBalance(finalBalanceCheck.balance);
            return;
        }

        setLoading(true);
        setError("");

        try {
            // Log de inicio de inversión
            LoggerService.log("business", "investment_started", {
                userId: currentUser.uid,
                opportunityId: opportunity.id,
                amount: parseFloat(amount),
                userBalance: userBalance,
                expectedReturn: opportunity.expectedReturn || 8
            });

            const investmentData = {
                userId: currentUser.uid,
                opportunityId: opportunity.id,
                opportunityTitle: opportunity.title,
                amount: parseFloat(amount),
                expectedReturn: opportunity.expectedReturn || 8,
                status: "pending",
                investmentDate: new Date().toISOString(),
                // Datos adicionales para tracking
                userBalanceBefore: userBalance,
                userBalanceAfter: userBalance - parseFloat(amount),
                opportunityStatus: opportunity.status,
                kycStatus: kycStatus
            };

            // Crear inversión
            const investmentId = await investmentService.createInvestment(investmentData);

            // Actualizar balance del usuario con verificación
            const newBalance = await usersService.updateUserBalance(currentUser.uid, -parseFloat(amount));
            
            // Verificar que la actualización fue exitosa
            if (newBalance === null || newBalance < 0) {
                throw new Error("Error al actualizar el balance del usuario");
            }

            // Crear transacción detallada
            await investmentService.createTransaction({
                userId: currentUser.uid,
                investmentId,
                amount: -parseFloat(amount),
                type: "investment",
                description: `Inversión en ${opportunity.title}`,
                status: "completed",
                metadata: {
                    opportunityId: opportunity.id,
                    expectedReturn: opportunity.expectedReturn,
                    userBalanceBefore: userBalance,
                    userBalanceAfter: newBalance
                }
            });

            // Log de auditoría detallado
            LoggerService.log("audit", "investment_completed", {
                action: "INVESTMENT_CREATED",
                userId: currentUser.uid,
                investmentId,
                opportunityId: opportunity.id,
                amount: parseFloat(amount),
                userBalanceBefore: userBalance,
                userBalanceAfter: newBalance,
                service: "InvestmentForm",
                timestamp: new Date().toISOString()
            });

            // Enviar email de confirmación
            try {
                await emailService.sendInvestmentConfirmation(currentUser.email, {
                    investmentId,
                    amount: parseFloat(amount),
                    opportunityTitle: opportunity.title,
                    expectedReturn: opportunity.expectedReturn,
                    userBalanceAfter: newBalance
                });
                
                LoggerService.log("business", "email_sent", {
                    type: "investment_confirmation",
                    userId: currentUser.uid,
                    investmentId
                });
            } catch (emailError) {
                LoggerService.log("system", "error", {
                    error: emailError.message,
                    service: "InvestmentForm",
                    context: "email_confirmation",
                    userId: currentUser.uid
                });
            }

            setConfirmedInvestment({
                ...investmentData,
                investmentId,
                userBalanceAfter: newBalance
            });
            setSuccess(t("investmentSuccess") || "¡Inversión realizada con éxito!");
            
            // Actualizar balance local
            setUserBalance(newBalance);

            if (onInvestmentComplete) {
                onInvestmentComplete(investmentId);
            }

        } catch (err) {
            setError(err.message || t("investmentError") || "Error al procesar la inversión");
            LoggerService.log("system", "error", {
                error: err.message,
                stack: err.stack,
                service: "InvestmentForm",
                context: "handleConfirmInvestment",
                userId: currentUser?.uid,
                opportunityId: opportunity?.id,
                amount: parseFloat(amount)
            });
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setAmount("");
        setError("");
        setSuccess("");
        setConfirmedInvestment(null);
        setTermsAccepted(false);
        setEligibilityChecked(false);
        if (onClose) {
            onClose();
        }
    };

    if (!eligibilityChecked && !loading) {
        return (
            <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogContent>
                    <Box display="flex" justifyContent="center" p={3}>
                        <CircularProgress />
                    </Box>
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Box display="flex" alignItems="center" gap={1}>
                    <AccountBalance />
                    Realizar Inversión
                </Box>
            </DialogTitle>
            
            <DialogContent>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}
                
                {success && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        {success}
                    </Alert>
                )}

                {!confirmedInvestment ? (
                    <>
                        {/* Información de la oportunidad */}
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="h6" gutterBottom>
                                {opportunity?.title}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                {opportunity?.description}
                            </Typography>
                            
                            <Grid container spacing={2} sx={{ mb: 2 }}>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        Retorno esperado
                                    </Typography>
                                    <Typography variant="h6" color="primary">
                                        {opportunity?.expectedReturn || 8}%
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        Duración
                                    </Typography>
                                    <Typography variant="h6">
                                        {opportunity?.duration || "12 meses"}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        {/* Información del usuario */}
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle1" gutterBottom>
                                Tu Información
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        Saldo disponible
                                    </Typography>
                                    <Typography variant="h6" color={userBalance > 0 ? "success.main" : "error.main"}>
                                        {formatCurrency(userBalance)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        Estado KYC
                                    </Typography>
                                    <Chip 
                                        label={kycStatus === "approved" ? "Aprobado" : "Pendiente"}
                                        color={kycStatus === "approved" ? "success" : "warning"}
                                        size="small"
                                    />
                                </Grid>
                            </Grid>
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        {/* Formulario de inversión */}
                        <Box>
                            <Typography variant="subtitle1" gutterBottom>
                                Detalles de la Inversión
                            </Typography>
                            
                            <TextField
                                fullWidth
                                label="Monto de inversión"
                                type="number"
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                margin="normal"
                                required
                                InputProps={{
                                    startAdornment: <InputAdornment position="start">€</InputAdornment>,
                                }}
                                helperText={`Mínimo: ${formatCurrency(opportunity?.minimumInvestment || 1000)} | Máximo: ${formatCurrency(opportunity?.maximumInvestment || 1000000)}`}
                            />

                            {amount && (
                                <Box sx={{ mt: 2, p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                        {t("investmentSummary") || "Resumen de la inversión:"}
                                    </Typography>
                                    
                                    <Grid container spacing={1}>
                                        <Grid item xs={6}>
                                            <Typography variant="body2" color="text.secondary">
                                                {t("amount") || "Monto"}
                                            </Typography>
                                            <Typography variant="body1" fontWeight="bold">
                                                {formatCurrency(parseFloat(amount) || 0)}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="body2" color="text.secondary">
                                                {t("expectedReturn") || "Retorno esperado"}
                                            </Typography>
                                            <Typography variant="body1" fontWeight="bold" color="primary">
                                                {((parseFloat(amount) || 0) * (opportunity?.expectedReturn || 8) / 100).toFixed(2)}€
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="body2" color="text.secondary">
                                                {t("remainingBalance") || "Saldo restante"}
                                            </Typography>
                                            <Typography 
                                                variant="body1" 
                                                fontWeight="bold"
                                                color={userBalance - (parseFloat(amount) || 0) < 100 ? "warning.main" : "success.main"}
                                            >
                                                {formatCurrency(userBalance - (parseFloat(amount) || 0))}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="body2" color="text.secondary">
                                                {t("investmentStatus") || "Estado"}
                                            </Typography>
                                            <Chip 
                                                label={validateInvestmentAmount(amount) ? "Error" : "Válido"}
                                                color={validateInvestmentAmount(amount) ? "error" : "success"}
                                                size="small"
                                            />
                                        </Grid>
                                    </Grid>
                                    
                                    {/* Mostrar advertencias si las hay */}
                                    {validateInvestmentAmount(amount) && (
                                        <Alert severity="warning" sx={{ mt: 2 }}>
                                            {validateInvestmentAmount(amount)}
                                        </Alert>
                                    )}
                                </Box>
                            )}

                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={termsAccepted}
                                        onChange={(e) => setTermsAccepted(e.target.checked)}
                                    />
                                }
                                label="Acepto los términos y condiciones de inversión"
                                sx={{ mt: 2 }}
                            />
                        </Box>
                    </>
                ) : (
                    /* Confirmación de inversión mejorada */
                    <Box textAlign="center" py={3}>
                        <CheckCircle color="success" sx={{ fontSize: 64, mb: 2 }} />
                        <Typography variant="h5" gutterBottom>
                            {t("investmentCompleted") || "¡Inversión Realizada!"}
                        </Typography>
                        <Typography variant="body1" color="text.secondary" paragraph>
                            {t("investmentProcessedSuccessfully") || "Tu inversión ha sido procesada correctamente."}
                        </Typography>
                        
                        <Box sx={{ mt: 3, p: 3, bgcolor: "success.light", borderRadius: 2 }}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        {t("investmentId") || "ID de Inversión"}
                                    </Typography>
                                    <Typography variant="body1" fontWeight="bold">
                                        {confirmedInvestment.investmentId}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        {t("amount") || "Monto"}
                                    </Typography>
                                    <Typography variant="body1" fontWeight="bold">
                                        {formatCurrency(confirmedInvestment.amount)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        {t("opportunity") || "Oportunidad"}
                                    </Typography>
                                    <Typography variant="body1" fontWeight="bold">
                                        {confirmedInvestment.opportunityTitle}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        {t("expectedReturn") || "Retorno Esperado"}
                                    </Typography>
                                    <Typography variant="body1" fontWeight="bold" color="primary">
                                        {confirmedInvestment.expectedReturn}%
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        {t("newBalance") || "Nuevo Saldo"}
                                    </Typography>
                                    <Typography variant="body1" fontWeight="bold" color="success.main">
                                        {formatCurrency(confirmedInvestment.userBalanceAfter)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        {t("investmentDate") || "Fecha de Inversión"}
                                    </Typography>
                                    <Typography variant="body1" fontWeight="bold">
                                        {new Date(confirmedInvestment.investmentDate).toLocaleDateString()}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Box>
                        
                        <Box sx={{ mt: 3, p: 2, bgcolor: "info.light", borderRadius: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                                {t("emailConfirmationSent") || "Recibirás un email de confirmación con todos los detalles."}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                {t("trackInvestment") || "Puedes hacer seguimiento de tu inversión en la sección 'Mis Inversiones'."}
                            </Typography>
                        </Box>
                    </Box>
                )}
            </DialogContent>
            
            <DialogActions>
                <Button onClick={handleClose} disabled={loading}>
                    {confirmedInvestment ? "Cerrar" : "Cancelar"}
                </Button>
                
                {!confirmedInvestment && (
                    <Button 
                        onClick={handleConfirmInvestment} 
                        variant="contained" 
                        disabled={loading || !amount || !termsAccepted}
                        startIcon={loading ? <CircularProgress size={20} /> : null}
                    >
                        {loading ? "Procesando..." : "Confirmar Inversión"}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default InvestmentForm; 